import { Injectable } from '@angular/core';
import type {
  InventoryTicket,
  ParsedTicketCancelledEvent,
  ParsedTicketCreatedEvent,
  ParsedWebSocketScanEvent,
  ScannedBy,
  TicketCancelledWebSocketMessage,
  TicketCreatedWebSocketMessage,
  TicketDisplayData,
  TicketInfo,
  TicketValidationResponse,
  WebSocketScanEvent,
} from '@seatix/check-in-domain';
import {
  SeatMinimal,
  TicketStatus,
  WebSocketEventType,
} from '@seatix/check-in-domain';
import type { SeatCategory, User } from '@seatix/shared/domain';

export interface ParsedSeat {
  row: string;
  section: string;
  number: string;
}

/**
 * Centralizes all ticket object creation and transformation logic
 *
 * Responsibilities:
 * - Parse seat_label format "01-G-13" into structured seat data
 * - Create ScannedBy from User objects
 * - Create SeatMinimal from various sources
 * - Map tickets between different formats
 * - Normalize tickets from any source to consistent format
 */
@Injectable({ providedIn: 'root' })
export class TicketMapperService {
  /**
   * Parses seat_label format "XX-XXX-XX" into structured seat
   * Example: "01-G-13" → {row: "01", section: "G", number: "13"}
   *
   * @param seatLabel Format: "X-X-X" (section-row-number)
   * @returns Parsed seat components or null if format is invalid
   */
  parseSeatLabel(seatLabel: string): ParsedSeat | null {
    if (!seatLabel) return null;

    const parts = seatLabel.split('-');
    if (parts.length !== 3) return null;

    const [section, row, number] = parts;
    if (!section || !row || !number) return null;

    return { row, section, number };
  }

  /**
   * Creates ScannedBy from a User object
   * Consolidates the common pattern of creating scan metadata
   */
  createScannedByInfo(
    user: User,
    gateId: number | null = null,
    gateName: string | null = null,
  ): ScannedBy {
    return {
      user_id: user.id,
      user_name: `${user.first_name} ${user.last_name}`.trim() || user.email,
      gate_id: gateId ?? 0,
      gate_name: gateName ?? '',
    };
  }

  /**
   * Creates ScannedBy from parsed data
   * Consolidates creation from different sources
   */
  createScannedByInfoRaw(
    userId: number,
    userName: string,
    gateId = 0,
    gateName = '',
  ): ScannedBy {
    return {
      user_id: userId,
      user_name: userName,
      gate_id: gateId,
      gate_name: gateName,
    };
  }

  /**
   * Creates SeatMinimal from structured data
   * Consolidates the common pattern of creating seat objects
   */
  createSeat(
    row: string,
    section: string,
    number: string,
    seatCategory: {
      id: number;
      name: string;
      color?: string;
    },
  ): SeatMinimal {
    return {
      row,
      section,
      number,
      seat_category: {
        id: seatCategory.id,
        name: seatCategory.name,
        color: seatCategory.color ?? '#FFF',
      },
    };
  }

  /**
   * Creates SeatMinimal from parsed seat_label and category
   * Combines seat parsing with category data
   */
  createSeatFromLabel(
    seatLabel: string,
    seatCategory?: Pick<SeatCategory, 'id' | 'name' | 'color'>,
  ): SeatMinimal | null {
    const parsed = this.parseSeatLabel(seatLabel);

    if (!parsed) return null;

    return this.createSeat(
      parsed.row,
      parsed.section,
      parsed.number,
      seatCategory || { id: 0, name: 'Uncategorized', color: '#FFF' },
    );
  }

  /**
   * Creates SeatMinimal from TicketInfo or validation response
   */
  createSeatFromTicketInfo(ticketInfo: TicketInfo): SeatMinimal {
    return {
      row: ticketInfo.seat.row,
      section: ticketInfo.seat.section,
      number: ticketInfo.seat.number,
      seat_category: {
        id: ticketInfo.seat.seat_category.id,
        name: ticketInfo.seat.seat_category.name,
        color: ticketInfo.seat.seat_category.color ?? '#FFF',
      },
    };
  }

  /**
   * Maps API validation response to TicketDisplayData
   * Handles both full responses (with ticket object) and top-level fields
   */
  mapValidationResponseToDisplay(
    response: TicketValidationResponse,
    source: 'api' | 'cache' = 'api',
  ): TicketDisplayData | null {
    const ticket = response.ticket;
    return {
      seat: ticket.seat,
      holderName: ticket.holder_name,
      source,
    };
  }

  /**
   * Parses WebSocket ticket.created message
   * Converts to InventoryTicket format for consistency with export endpoint
   */
  parseTicketCreatedMessage(
    message: TicketCreatedWebSocketMessage,
  ): ParsedTicketCreatedEvent | null {
    try {
      const { data } = message;

      const ticket_id = Number(data.ticket_id);

      const inventoryTicket = {
        ticket_id,
        holder_name: data.holder_name,
        seat_label: data.seat_label,
        status: data.status as TicketStatus,
        signature: data.signature,
        seat_category_id: Number(data.seat_category_id),
        seat: {
          row: data.seat.row,
          section: data.seat.section,
          number: data.seat.number,
          seat_category: data.seat.seat_category,
        },
        seat_category: data.seat.seat_category
          ? {
              id: Number(data.seat.seat_category.id),
              name: data.seat.seat_category.name,
              color: data.seat.seat_category.color,
            }
          : null,
      } as unknown as InventoryTicket;

      return {
        type: WebSocketEventType.TicketCreated,
        data: inventoryTicket,
        event_id: String(data.event_id),
        timestamp: data.timestamp,
      };
    } catch (error) {
      console.error('Failed to parse ticket.created message:', error, message);
      return null;
    }
  }

  /**
   * Parses WebSocket ticket.scanned message
   * Maps to ParsedWebSocketScanEvent format
   */
  parseTicketScannedMessage(
    message: WebSocketScanEvent,
  ): ParsedWebSocketScanEvent | null {
    if (message.type !== 'ticket.scanned') {
      return null;
    }

    try {
      const { data } = message;

      const parsedEvent: ParsedWebSocketScanEvent = {
        type: 'ticket.scanned',
        ticket_id: data.ticket_id,
        holder_name: data.holder_name,
        seat_label: data.seat_label,
        status: TicketStatus.Scanned,
        scan_id: data.scan_id,
        device_id: data.device_id,
        scanned_at: data.timestamp,
        scanned_by: data.scanned_by,
        seat: data.seat,
        timestamp: data.timestamp,
      };

      return parsedEvent;
    } catch (error) {
      console.error('Failed to parse ticket.scanned message:', error, message);
      return null;
    }
  }

  /**
   * Parses WebSocket ticket.cancelled message
   * Similar to ticket.created but includes cancelled_at timestamp
   */
  parseTicketCancelledMessage(
    message: TicketCancelledWebSocketMessage,
  ): ParsedTicketCancelledEvent | null {
    try {
      const { data } = message;
      const ticket_id = Number(data.ticket_id);

      const inventoryTicket = {
        ticket_id,
        holder_name: data.holder_name,
        seat_label: data.seat_label,
        status: TicketStatus.Cancelled,
        signature: data.signature,
        seat_category_id: Number(data.seat_category_id),
        cancelled_at: Number(data.cancelled_at),
        seat: {
          row: data.seat.row,
          section: data.seat.section,
          number: data.seat.number,
          seat_category: data.seat.seat_category,
        },
        seat_category: data.seat.seat_category
          ? {
              id: Number(data.seat.seat_category.id),
              name: data.seat.seat_category.name,
              color: data.seat.seat_category.color,
            }
          : null,
      } as unknown as InventoryTicket;

      return {
        type: WebSocketEventType.TicketCancelled,
        data: inventoryTicket,
        cancelled_at: Number(data.cancelled_at),
        event_id: String(data.event_id),
        timestamp: data.timestamp,
      };
    } catch (error) {
      console.error(
        'Failed to parse ticket.cancelled message:',
        error,
        message,
      );
      return null;
    }
  }
}
